import { useState, useEffect } from "react";
import { X, Building, Calendar, DollarSign, FileText, Edit, Save, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import { 
  obtenerZonasComunes, 
  modificarZonaComun,
  obtenerEstatusZonaComun,
  ZonaComun,
  EstatusZonaComun 
} from "../../../../api/zonaComunService";
import { obtenerTodosDiasUtilizacion, DiaUtilizacion } from "../../../../api/zonaComunHorarioService";
import GaleriaImagenesModal from "./GaleriaImagenesModal";

interface DetailZonaComunModalProps {
  isOpen: boolean;
  zonaComunCodigo: string;
  onClose: (shouldReload: boolean) => void;
}

export default function DetailZonaComunModal({ 
  isOpen, 
  zonaComunCodigo,
  onClose 
}: DetailZonaComunModalProps) {
  const { session } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showGaleria, setShowGaleria] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const [zonaComun, setZonaComun] = useState<ZonaComun | null>(null);
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [selectedDiaMantenimiento, setSelectedDiaMantenimiento] = useState("");
  const [valorAlquiler, setValorAlquiler] = useState("");
  const [selectedEstatus, setSelectedEstatus] = useState("");
  
  const [diasUtilizacion, setDiasUtilizacion] = useState<DiaUtilizacion[]>([]);
  const [estatusZonaComun, setEstatusZonaComun] = useState<EstatusZonaComun[]>([]);
  const [isLoadingCatalogos, setIsLoadingCatalogos] = useState(false);

  useEffect(() => {
    if (isOpen && zonaComunCodigo) {
      loadZonaComun();
      loadCatalogos();
      setIsEditing(false);
      setImageError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, zonaComunCodigo]);

  const loadCatalogos = async () => {
    try {
      setIsLoadingCatalogos(true);
      const [diasResponse, estatusResponse] = await Promise.all([
        obtenerTodosDiasUtilizacion(),
        obtenerEstatusZonaComun()
      ]);
      
      if (diasResponse.code === "1" && diasResponse.object_) {
        setDiasUtilizacion(diasResponse.object_);
      }
      
      if (estatusResponse.code === "1" && estatusResponse.object_) {
        setEstatusZonaComun(estatusResponse.object_);
      }
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
    } finally {
      setIsLoadingCatalogos(false);
    }
  };

  const loadZonaComun = async () => {
    if (!zonaComunCodigo) return;

    try {
      setIsLoading(true);
      const unidadCodigo = session?.unidad?.id || "";
      
      if (!unidadCodigo) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo obtener el código de unidad residencial",
          confirmButtonColor: "#d33",
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10000';
            const container = popup.closest('.swal2-container');
            if (container) {
              (container as HTMLElement).style.zIndex = '10000';
            }
          },
        });
        return;
      }

      const payload = {
        unidad_residencial_codigo: unidadCodigo,
        zona_comun_codigo: zonaComunCodigo,
      };

      const response = await obtenerZonasComunes(payload);

      if (response.code === "1" && response.object_ && response.object_.length > 0) {
        const zona = response.object_[0];
        setZonaComun(zona);
        setNombre(zona.zco_nombre);
        setDescripcion(zona.zco_descripcion);
        setSelectedDiaMantenimiento(zona.zco_dia_mantenimiento);
        setValorAlquiler(zona.zco_valor_alquiler.toString());
        setSelectedEstatus(zona.zco_estatus);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo cargar la información de la zona común",
          confirmButtonColor: "#d33",
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10000';
            const container = popup.closest('.swal2-container');
            if (container) {
              (container as HTMLElement).style.zIndex = '10000';
            }
          },
        });
      }
    } catch (error) {
      console.error("Error al cargar zona común:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar la información de la zona común",
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!nombre.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese el nombre de la zona común",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!descripcion.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese la descripción de la zona común",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!selectedDiaMantenimiento) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor seleccione el día de mantenimiento",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!valorAlquiler || valorAlquiler.trim() === "" || isNaN(parseFloat(valorAlquiler)) || parseFloat(valorAlquiler) < 0) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese un valor de alquiler válido (puede ser 0)",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!selectedEstatus) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor seleccione el estatus",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        zona_comun_codigo: zonaComunCodigo,
        nombre_zona_comun: nombre.trim(),
        descripcion_zona_comun: descripcion.trim(),
        valor_alquiler: parseFloat(valorAlquiler),
        dia_mantenimiento_codigo: selectedDiaMantenimiento,
        estatus_codigo: selectedEstatus,
      };

      const response = await modificarZonaComun(payload);

      if (response.code === "1" || response.code === 1) {
        Swal.fire({
          icon: "success",
          title: "¡Zona común actualizada!",
          text: response.message || "La zona común se ha actualizado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10000';
            const container = popup.closest('.swal2-container');
            if (container) {
              (container as HTMLElement).style.zIndex = '10000';
            }
          },
        });
        setIsEditing(false);
        loadZonaComun(); // Recargar datos actualizados
        onClose(true);
      } else {
        throw new Error(response.message || "Error al actualizar la zona común");
      }
    } catch (error) {
      console.error("Error al actualizar zona común:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al actualizar la zona común";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseGaleria = (updated: boolean) => {
    setShowGaleria(false);
    if (updated) {
      loadZonaComun(); // Recargar para actualizar la imagen principal
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget && !isSubmitting) {
            onClose(false);
          }
        }}
      >
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] flex flex-col">
          <div className="sticky top-0 bg-gradient-to-r from-military-500 to-military-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between z-10">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Building size={28} />
              {isEditing ? "Editar Zona Común" : "Detalle Zona Común"}
            </h2>
            <button
              onClick={() => onClose(false)}
              className="text-white hover:bg-white/20 rounded-full p-1 transition"
              disabled={isSubmitting}
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-12 w-12 animate-spin text-military-600 mb-4" />
                <p className="text-gray-600">Cargando información...</p>
              </div>
            ) : !zonaComun ? (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-gray-600">No se pudo cargar la información</p>
              </div>
            ) : (
              <>
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 shadow-inner">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-base font-bold text-gray-800">
                      <ImageIcon size={20} className="inline mr-2 text-military-600" />
                      Imagen Principal
                    </label>
                    <Button
                      type="button"
                      onClick={() => setShowGaleria(true)}
                      variant="outline"
                      className="text-sm border-2 border-military-500 text-military-600 hover:bg-military-50 font-semibold"
                      disabled={isSubmitting}
                    >
                      Ver Todas las Imágenes
                    </Button>
                  </div>
                  <div className="w-full h-80 overflow-hidden bg-white rounded-xl shadow-lg border-4 border-white flex items-center justify-center group relative">
                    {zonaComun.zco_ruta_foto_principal && !imageError ? (
                      <>
                        <img
                          src={zonaComun.zco_ruta_foto_principal}
                          alt={zonaComun.zco_nombre}
                          className="max-w-full max-h-full object-contain transition-transform duration-300"
                          onError={() => setImageError(true)}
                        />
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-400 p-8">
                        <div className="bg-gray-200 rounded-full p-6 mb-4">
                          <Building size={64} className="text-gray-400" />
                        </div>
                        <span className="text-base font-medium">Sin imagen disponible</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 p-5 shadow-sm">
                  <label className="block text-base font-bold text-gray-800 mb-3">
                    <Building size={18} className="inline mr-2 text-military-600" />
                    Nombre *
                  </label>
                  {isEditing ? (
                    <Input
                      type="text"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      disabled={isSubmitting}
                      className="w-full p-3.5 shadow-sm border-2 focus:border-military-500 rounded-lg text-base"
                    />
                  ) : (
                    <p className="text-gray-900 p-4 bg-white rounded-lg border border-gray-200 font-semibold text-lg">{nombre}</p>
                  )}
                </div>

                <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 p-5 shadow-sm">
                  <label className="block text-base font-bold text-gray-800 mb-3">
                    <FileText size={18} className="inline mr-2 text-military-600" />
                    Descripción *
                  </label>
                  {isEditing ? (
                    <Textarea
                      value={descripcion}
                      onChange={(e) => setDescripcion(e.target.value)}
                      disabled={isSubmitting}
                      className="w-full p-3.5 shadow-sm border-2 focus:border-military-500 rounded-lg min-h-[120px] text-base"
                    />
                  ) : (
                    <p className="text-gray-800 p-4 bg-white rounded-lg border border-gray-200 min-h-[120px] whitespace-pre-wrap leading-relaxed">{descripcion}</p>
                  )}
                </div>

                <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 p-5 shadow-sm">
                  <label className="block text-base font-bold text-gray-800 mb-3">
                    <Calendar size={18} className="inline mr-2 text-military-600" />
                    Día de Mantenimiento *
                  </label>
                  {isEditing ? (
                    <select
                      value={selectedDiaMantenimiento}
                      onChange={(e) => setSelectedDiaMantenimiento(e.target.value)}
                      disabled={isSubmitting || isLoadingCatalogos}
                      className="w-full rounded-lg border-2 border-gray-300 focus:border-military-500 focus:ring-military-500 p-3.5 bg-white shadow-sm text-base font-medium"
                    >
                      <option value="">Seleccionar...</option>
                      {diasUtilizacion.map((dia) => (
                        <option key={dia.ddu_codigo} value={dia.ddu_codigo}>
                          {dia.ddu_nombre}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-gray-900 p-4 bg-white rounded-lg border border-gray-200 font-semibold text-base">
                      {zonaComun.zco_dia_mantenimiento_nombre}
                    </p>
                  )}
                </div>

                <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 p-5 shadow-sm">
                  <label className="block text-base font-bold text-gray-800 mb-3">
                    <DollarSign size={18} className="inline mr-2 text-military-600" />
                    Costo del Alquiler *
                  </label>
                  {isEditing ? (
                    <Input
                      type="number"
                      value={valorAlquiler}
                      onChange={(e) => setValorAlquiler(e.target.value)}
                      disabled={isSubmitting}
                      className="w-full p-3.5 shadow-sm border-2 focus:border-military-500 rounded-lg text-base"
                      min="0"
                      step="1000"
                    />
                  ) : (
                    <p className="text-gray-900 p-4 bg-white rounded-lg border border-gray-200 font-bold text-xl text-green-700">
                      ${parseFloat(valorAlquiler).toLocaleString('es-CO')}
                    </p>
                  )}
                </div>

                <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 p-5 shadow-sm">
                  <label className="block text-base font-bold text-gray-800 mb-3">
                    Estatus *
                  </label>
                  {isEditing ? (
                    <select
                      value={selectedEstatus}
                      onChange={(e) => setSelectedEstatus(e.target.value)}
                      disabled={isSubmitting || isLoadingCatalogos}
                      className="w-full rounded-lg border-2 border-gray-300 focus:border-military-500 focus:ring-military-500 p-3.5 bg-white shadow-sm text-base font-medium"
                    >
                      <option value="">Seleccionar...</option>
                      {estatusZonaComun.map((estatus) => (
                        <option key={estatus.ezc_codigo} value={estatus.ezc_codigo}>
                          {estatus.ezc_nombre}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-gray-900 p-4 bg-white rounded-lg border border-gray-200">
                      <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${
                        zonaComun.zco_estatus === "D" 
                          ? "bg-green-100 text-green-800 border-2 border-green-300" 
                          : zonaComun.zco_estatus === "M"
                          ? "bg-yellow-100 text-yellow-800 border-2 border-yellow-300"
                          : "bg-red-100 text-red-800 border-2 border-red-300"
                      }`}>
                        {zonaComun.zco_estatus_nombre}
                      </span>
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
          <div className="max-w-4xl mx-auto flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (isEditing) {
                  setIsEditing(false);
                  if (zonaComun) {
                    setNombre(zonaComun.zco_nombre);
                    setDescripcion(zonaComun.zco_descripcion);
                    setSelectedDiaMantenimiento(zonaComun.zco_dia_mantenimiento);
                    setValorAlquiler(zonaComun.zco_valor_alquiler.toString());
                    setSelectedEstatus(zonaComun.zco_estatus);
                  }
                } else {
                  onClose(false);
                }
              }}
              disabled={isSubmitting}
              className="flex-1 border-2 hover:bg-gray-50 py-2.5"
            >
              {isEditing ? "Cancelar" : "Cerrar"}
            </Button>
            {!isEditing ? (
              <Button
                type="button"
                onClick={() => setIsEditing(true)}
                className="flex-1 bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold flex items-center justify-center gap-2 py-2.5"
              >
                <Edit size={18} />
                Editar
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting}
                className="flex-1 bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold flex items-center justify-center gap-2 py-2.5"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Guardar Cambios
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
        </div>
      </div>

      <GaleriaImagenesModal
        isOpen={showGaleria}
        zonaComunCodigo={zonaComunCodigo}
        onClose={handleCloseGaleria}
      />
    </>
  );
}

